import type { BankAccount } from '../types/account';

const STORAGE_KEY = 'debtracker_accounts';

const generateId = (): string =>
  Math.random().toString(36).substring(2, 10) + Date.now().toString(36);

export const getAllAccounts = (): BankAccount[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as BankAccount[]) : [];
  } catch {
    return [];
  }
};

const saveAll = (accounts: BankAccount[]) =>
  localStorage.setItem(STORAGE_KEY, JSON.stringify(accounts));

export const createAccount = (
  data: Omit<BankAccount, 'id' | 'createdAt' | 'updatedAt'>
): BankAccount => {
  const now = new Date().toISOString();
  const account: BankAccount = { ...data, id: generateId(), createdAt: now, updatedAt: now };
  saveAll([...getAllAccounts(), account]);
  return account;
};

export const updateAccount = (
  id: string,
  updates: Partial<Omit<BankAccount, 'id' | 'createdAt'>>
): BankAccount | null => {
  const all = getAllAccounts();
  const idx = all.findIndex((a) => a.id === id);
  if (idx === -1) return null;
  all[idx] = { ...all[idx], ...updates, updatedAt: new Date().toISOString() };
  saveAll(all);
  return all[idx];
};

export const deleteAccount = (id: string): boolean => {
  const all = getAllAccounts();
  const filtered = all.filter((a) => a.id !== id);
  if (filtered.length === all.length) return false;
  saveAll(filtered);
  return true;
};

// Assuming base currency is USD for aggregation if mixed.
// For now, simple aggregation per currency if needed, or just total sum.
export const getTotalBalanceByCurrency = (): Record<string, number> => {
  const all = getAllAccounts();
  const totals: Record<string, number> = {};
  for (const acc of all) {
    if (!totals[acc.currency]) totals[acc.currency] = 0;
    totals[acc.currency] += acc.balance;
  }
  return totals;
};
